import { HTMLAttributes, Key, SyntheticEvent, useEffect, useState } from "react";
import Autocomplete, { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getListTransactions } from "../../services";
import { TransactionListRow } from "../../types/sale.type";
import { formatNumber } from "../../utils";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { formatOrderDate } from "../SpecialOrder/data";

interface InvoiceSearchProps {
  /** The branch head office searches in; a branch user's search is pinned by the server. */
  companyId?: string;
  disabled: boolean;
  error?: string;
  helperText?: string;
  onSelect: (row: TransactionListRow | null) => void;
}

const SEARCH_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

const optionLabel = (row: TransactionListRow) => row.transaction_no;
const isSameRow = (option: TransactionListRow, selected: TransactionListRow) =>
  option.uuid === selected.uuid;
const keepServerOrder = (options: TransactionListRow[]) => options;

function InvoiceOption({
  optionProps,
  row,
}: Readonly<{
  optionProps: HTMLAttributes<HTMLLIElement> & { key: Key };
  row: TransactionListRow;
}>) {
  const { key, ...rest } = optionProps;
  return (
    <li key={key} {...rest}>
      <Stack>
        <Typography variant="body2" fontWeight={600}>
          {row.transaction_no} · {formatNumber(row.grand_total)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatOrderDate(row.transaction_date)} · {row.customer_code} {row.customer_name}
        </Typography>
      </Stack>
    </li>
  );
}

/**
 * Finds the original bill by its number, over the same `GET /transactions?transaction_no=`
 * prefix search the sales list uses.
 */
export default function InvoiceSearch({
  companyId,
  disabled,
  error,
  helperText,
  onSelect,
}: Readonly<InvoiceSearchProps>) {
  const [input, setInput] = useState<string>("");
  const [options, setOptions] = useState<TransactionListRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const query = useDebounce(input.trim(), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (disabled || query.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const {
          data: { data },
        } = await getListTransactions({
          page: 1,
          limit: SEARCH_LIMIT,
          transactionNo: query,
          companyId,
        });
        if (active) setOptions(data?.rows ?? []);
      } catch {
        // The interceptor already shows why the search failed.
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [query, companyId, disabled]);

  const handleInputChange = (_event: SyntheticEvent, text: string) => setInput(text);
  const handleChange = (_event: SyntheticEvent, row: TransactionListRow | null) => onSelect(row);
  const renderOption = (
    optionProps: HTMLAttributes<HTMLLIElement> & { key: Key },
    row: TransactionListRow,
  ) => <InvoiceOption key={row.uuid} optionProps={optionProps} row={row} />;
  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      size="small"
      label="เลขที่ใบกำกับภาษีเดิม"
      placeholder="พิมพ์เลขที่บิลอย่างน้อย 3 ตัวอักษร"
      error={Boolean(error)}
      helperText={error || helperText}
    />
  );

  const noOptionsText =
    input.trim().length < MIN_SEARCH_LENGTH ? "พิมพ์เลขที่บิลอย่างน้อย 3 ตัวอักษร" : "ไม่พบบิล";

  return (
    <Autocomplete<TransactionListRow, false, false, false>
      options={options}
      disabled={disabled}
      loading={loading}
      loadingText="กำลังค้นหาบิล..."
      noOptionsText={noOptionsText}
      filterOptions={keepServerOrder}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={isSameRow}
      onInputChange={handleInputChange}
      onChange={handleChange}
      renderOption={renderOption}
      renderInput={renderInput}
    />
  );
}
